import React from 'react';
import Amplify from 'aws-amplify';
import { Auth } from 'aws-amplify';

import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  split,
  defaultDataIdFromObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { WebSocketLink } from '@apollo/client/link/ws';

import { StyledEngineProvider } from '@mui/material/styles';

import jwt_decode from 'jwt-decode';

import { ThemeProvider } from '@mui/material/styles';
import fetch from 'isomorphic-fetch';
import { SnackbarProvider } from 'notistack';

import CssBaseline from '@mui/material/CssBaseline';
import theme from 'theme/theme';

import { AuthProvider } from 'components/authorization';

import * as Sentry from '@sentry/gatsby';

// These are NOPs for SSR only, replaced on the client
let authLink = setContext((_, { headers }) => headers);
let resetHasuraAuth = () => undefined;
let wsLink = {};

const getAuthHeaders = async () => {
  let token;
  try {
    // An "error" is expected when users don't have Cognito stuff in localstore
    const data = await Auth.currentSession();
    token = data?.getIdToken().getJwtToken();
  } catch (err) {
    console.log('Expected error getting jwt for current session', err);
  }

  // If no role, hasura will give the request an "anonymous" role since we have
  // in its config - HASURA_GRAPHQL_UNAUTHORIZED_ROLE: anonymous.  Source of truth for role is
  // in the JWT if we have it - only ever have 1 allowed role, so if we have a JWT then get it
  // from there.
  if (token) {
    const decoded = jwt_decode(token);
    const claimString = decoded['https://hasura.io/jwt/claims'];
    const claims = JSON.parse(claimString);
    const role = claims['x-hasura-allowed-roles'][0];

    return {
      Authorization: `Bearer ${token}`,
      'X-Hasura-Role': role,
      'Hasura-Role': role, // Hasura strips out X-Hasura-Role (and any X-) when calling webhook
    };
  }
  return {};
};

if (typeof window !== 'undefined') {
  Amplify.configure({
    Auth: {
      region: process.env.GATSBY_COGNITO_REGION,
      userPoolId: process.env.GATSBY_COGNITO_USER_POOL_ID,
      userPoolWebClientId: process.env.GATSBY_COGNITO_CLIENT_ID,
      authenticationFlowType: 'USER_PASSWORD_AUTH',
    },
  });

  // Pull the Cognito token to get our claims so we can send to Hasura
  authLink = setContext(async (_, { headers }) => {
    const authHeaders = await getAuthHeaders();

    return {
      headers: {
        ...headers,
        ...authHeaders,
      },
    };
  });

  wsLink = new WebSocketLink({
    uri: process.env.GATSBY_API_URL.replace('https', 'wss').replace(
      'http',
      'ws',
    ),
    options: {
      reconnect: true,
      connectionParams: async () => {
        console.log('resetting link');
        return { headers: await getAuthHeaders() };
      },
    },
  });

  // Since the connectionParams cannot be dynamic, allow others to reset it
  resetHasuraAuth = () => wsLink.subscriptionClient.close(false, false);
}

const httpLink = createHttpLink({
  uri: process.env.GATSBY_API_URL,
  credentials: 'include',
  fetch,
});
const authedHttpLink = authLink.concat(httpLink);

// https://hasura.io/docs/latest/graphql/core/guides/integrations/apollo-subscriptions.html
// Per https://www.apollographql.com/docs/react/data/subscriptions/#1-install-required-libraries
// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value

const client = new ApolloClient({
  link:
    typeof window !== 'undefined'
      ? split(
          ({ query }) => {
            const definition = getMainDefinition(query);
            return (
              definition.kind === 'OperationDefinition' &&
              definition.operation === 'subscription'
            );
          },
          wsLink,
          authedHttpLink,
        )
      : authedHttpLink,
  cache: new InMemoryCache({
    dataIdFromObject(responseObject) {
      // purchase requests read/update have different __typenames
      // so a custom cache normalization function is needed for them to update correctly.
      if (
        [
          'purchase_requests_list',
          'purchase_for_location',
          'CreateMsoPurchaseRequestInvoiceOutput',
        ].includes(responseObject.__typename)
      ) {
        return `PurchaseRequest:${responseObject.id}`;
      } else if (
        responseObject.__typename === 'UpdatePartnerStripeUserToChargeOutput'
      ) {
        return `partner:${responseObject.id}`;
      } else if (responseObject.__typename === 'CreateNewMsoOutput') {
        return `partner_basic:${responseObject.id}`;
      } else if (
        [
          'provider_direct_product_purchase',
          'provider_direct_purchase_web_admin_view',
        ].includes(responseObject.__typename)
      ) {
        return `provider_direct_purchase_web_admin_view:${responseObject.id}`;
      } else {
        return defaultDataIdFromObject(responseObject);
      }
    },
    typePolicies: {
      location_product: {
        keyFields: ['location', 'product'],
      },
      user_location: {
        keyFields: ['user', 'location'],
      },
      model_product: {
        keyFields: ['model', 'product'],
      },
      rental_volume_discount: {
        keyFields: ['location', 'hours'],
      },
      accreditation_location_privilege: {
        keyFields: ['location', 'accreditation'],
      },
      Query: {
        fields: {
          booking: {
            merge(existing, incoming) {
              return incoming;
            },
          },
        },
      },
    },
    connectToDevTools: process.env.NODE_ENV === 'development',
  }),
});

const snackBarOptions = {
  maxSnack: 3,
  autoHideDuration: 7000,
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

const ENVIRONMENT = process.env.GATSBY_ENVIRONMENT;
const SENTRY_DSN = process.env.GATSBY_SENTRY_DSN;

// values mostly copied from app
Sentry.init({
  dsn: SENTRY_DSN,
  integrations: [new Sentry.BrowserTracing(), new Sentry.Replay()],
  debug: ENVIRONMENT != 'production',
  environment: ENVIRONMENT,
  tracesSampleRate: 0.01,
});

export const GatsbyTopLayout = ({ children }) => {
  return (
    <ApolloProvider client={client}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <SnackbarProvider {...snackBarOptions}>
            <AuthProvider resetAuth={resetHasuraAuth}>
              <CssBaseline>{children}</CssBaseline>
            </AuthProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ApolloProvider>
  );
};
